import { phoneExtensions } from 'src/assets/international-phone-extensions/int-phone-extensions';
import { ISO_COUNTRY_NAMES_EN } from './iso-country-names';

/**
 * Shared visual + formatting constants for the CEO area.
 *
 * CEO_SERIES is a fixed categorical order — assign hues by slot, never cycle them, so a
 * series keeps its colour when filters change the series count. Validated for colour-vision
 * deficiency (worst adjacent pair ΔE 8.9 protan, normal-vision floor 23.8) against a light
 * surface; the low-contrast slots are always paired with a legend, axis labels and a tooltip.
 */
export const CEO_SERIES = ['#2563eb', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];

/** Single hue for magnitude-by-category (plan mix, country counts). */
export const CEO_SEQUENTIAL = '#2563eb';

export const CEO_INK = {
    grid: 'rgba(15, 23, 42, 0.06)',
    tick: '#64748b',
    tooltipBg: '#0f172a',
};

/** Plan badge colours. Kept apart from CEO_SERIES: these encode identity, not a series slot. */
export const PLAN_STYLES: Record<string, string> = {
    free: 'bg-slate-100 text-slate-600 border-slate-200',
    starter: 'bg-sky-50 text-sky-700 border-sky-200',
    shared: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    profesional: 'bg-violet-50 text-violet-700 border-violet-200',
    enterprise: 'bg-amber-50 text-amber-700 border-amber-200',
    api: 'bg-teal-50 text-teal-700 border-teal-200',
    /**
     * ⭐ NOT A PLAN — a project built in the platform UI, which is stored as
     * `plan: 'api'` like every other VCaaS project. Given its own colour so the two
     * kinds are distinguishable at a glance; see `TotalumInstanceI.createdVia`.
     */
    ui: 'bg-blue-50 text-blue-700 border-blue-200',
    agent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function planStyle(plan?: string): string {
    return PLAN_STYLES[plan || ''] || 'bg-slate-100 text-slate-600 border-slate-200';
}

/**
 * What to put on a project's chip: its plan, except that a project built in the
 * platform UI says `'ui'` instead of the `'api'` it is stored as.
 *
 * ⚠️ DISPLAY ONLY. `instances.plan` really is `'api'` for both kinds and has to
 * stay that way — `totalum-backend` branches on it for usage limits, prompt-security
 * validation and agent behaviour. This function exists so the CEO pages can tell a
 * customer who *built something* apart from one who *integrated the API*, without
 * anything downstream noticing.
 *
 * ⚠️ ONLY AN EXPLICIT `'ui'` CHANGES THE LABEL. `createdVia` is absent on every
 * project created before it existed and there is no way to recover the answer
 * afterwards, so those keep saying `api` rather than being guessed at.
 */
export function projectTypeLabel(project?: { plan?: string; createdVia?: string } | null): string {
    if (!project) return '';
    if (project.plan === 'api' && project.createdVia === 'ui') return 'ui';
    return project.plan || '';
}

/**
 * What the NEW PLATFORM calls each account plan, and what it grants per month.
 *
 * ⚠️ `shared` IS DISPLAYED AS "Basic", and that mismatch is deliberate — it exists
 * in the legacy product too, and the platform's own billing page says "Basic". An
 * admin comparing this table against what the customer sees must read the same
 * word. Mirrors `PLATFORM_PLAN_NAMES` / `PLATFORM_PLAN_CREDITS` in account-backend's
 * `common/constants/pricing.ts`; those stay authoritative — this is display only,
 * and the grantable list comes from the server with the usage response.
 */
export const PLATFORM_PLAN_LABELS: Record<string, string> = {
    free: 'Free',
    starter: 'Starter',
    shared: 'Builder',
    profesional: 'Professional',
    enterprise: 'Enterprise',
};

export const PLATFORM_PLAN_CREDITS: Record<string, number> = {
    free: 0,
    starter: 300,
    shared: 600,
    profesional: 1200,
    enterprise: 5000,
};

export function platformPlanLabel(plan?: string): string {
    if (!plan) return '';
    return PLATFORM_PLAN_LABELS[plan] || plan;
}

/**
 * ═══ ISO-3166 alpha-2 → ENGLISH COUNTRY NAME ════════════════════════════════
 *
 * Two layers: the full committed ISO table (`iso-country-names.ts`), with the
 * country dataset the profile and phone-extension pickers already use spread OVER
 * it — so a country reads the same in both places, and the 62 regions that dataset
 * omits still get a name.
 *
 * `Intl.DisplayNames` was the alternative and was rejected: this app's
 * `.browserslistrc` still targets Chrome 79 and Safari 14.0, where it does not
 * exist and throws on construction.
 */
const COUNTRY_NAMES_BY_CODE: Record<string, string> = phoneExtensions.reduce(
    (map: Record<string, string>, entry: any) => {
        if (entry?.code_2 && entry?.name_en) map[String(entry.code_2).toUpperCase()] = entry.name_en;
        return map;
    },
    /**
     * ⚠️⚠️ THE ISO TABLE IS THE BASE LAYER, AND IT HAD TO BE ADDED.
     *
     * The phone dataset carries **189 of the 251 regions** and is missing almost
     * all of Africa, so the users table showed raw codes — EG, AO, NG, KE, GN, ET
     * — for exactly the markets that have been growing. The comment below used to
     * call that an acceptable fallback for "a rare code"; live data says otherwise.
     *
     * ⚠️ THE DATASET STILL WINS, because it is spread over the base rather than
     * under it: where the phone picker has a name, the CEO page says the same
     * thing, and the ISO table only fills the holes.
     */
    { ...ISO_COUNTRY_NAMES_EN } as Record<string, string>,
);

/**
 * The country as a human reads it.
 *
 * ⚠️⚠️ ONLY A TWO-LETTER VALUE IS MAPPED, AND THAT GUARD IS THE WHOLE FUNCTION.
 * `users.country` is a HETEROGENEOUS column: it is the free-text invoicing country
 * the customer types ("Spain", "España", the legacy literal "spain"), except that
 * the platform's `backfillCountry()` writes an ISO-2 code into it when the profile
 * form was never filled in — which is exactly why the CEO table shows "ES", "NZ",
 * "US", "LT". Mapping unconditionally would look up "España" in a code table, miss,
 * and blank out every legacy customer. `users.acquisition.country` is pure ISO-2
 * and could be mapped unconditionally, but one honest function for both is better
 * than two that must be applied to the right column.
 *
 * ⚠️ AN UNKNOWN CODE STILL FALLS BACK TO ITSELF — but that is now a genuinely
 * unknown code (a typo, a deprecated value like "UK"), not the 62 real countries
 * the phone dataset happens to omit. See `iso-country-names.ts`.
 */
export function countryName(value?: string | null): string {
    const raw = (value || '').trim();
    if (!raw) return '';
    if (!/^[A-Za-z]{2}$/.test(raw)) return raw;
    return COUNTRY_NAMES_BY_CODE[raw.toUpperCase()] || raw.toUpperCase();
}

/**
 * ═══ ACQUISITION CHANNEL CHIPS — shared by the users table and the dashboard ═══
 *
 * `kind` drives the colour and comes from the MEASURED signals, never from the
 * channel name: 'Google' is both the ad click and the organic search, which is
 * exactly what makes the name groupable (see `AcquisitionIdentityI` on the backend).
 *   paid · referral · self (the user's own answer) · direct · organic (per-host hue)
 */
export type AcquisitionChipKind = 'paid' | 'referral' | 'self' | 'direct' | 'organic';

/** The marker `acquisitionCampaign` carries when the channel is the user's own answer. */
export const SELF_REPORTED_CAMPAIGN = 'self-reported';

/** Tailwind classes for the acquisition channel chip, keyed by kind. */
export function acquisitionChipClass(kind: string): string {
    switch (kind) {
        case 'paid': return 'bg-amber-50 text-amber-700 border-amber-200';
        case 'referral': return 'bg-violet-50 text-violet-700 border-violet-200';
        // The user told us themselves — a different KIND of fact, so a different colour.
        case 'self': return 'bg-sky-50 text-sky-700 border-sky-200 border-dashed';
        // 'organic' (a website / referrer) is coloured per-host instead — see hostColor().
        case 'organic': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        default: return 'bg-slate-100 text-slate-500 border-slate-200';
    }
}

/**
 * Deterministic inline colour for a referrer/website chip, so each distinct site is instantly
 * recognisable. The hue is hashed from the host name across the full 360° wheel (not a small
 * fixed palette — that collided), giving hundreds of separable colours. Reserved semantic hues
 * are steered around so a website colour never reads as paid (amber) or referral (violet); the
 * chip is a light tint with matching dark text so it stays readable.
 */
export function hostColor(name: string): { [k: string]: string } {
    const key = (name || '').toLowerCase();
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    let hue = hash % 360;
    // Keep clear of the reserved bands: amber (paid ≈ 40°) and violet (referral ≈ 273°).
    if (hue >= 28 && hue <= 52) hue = (hue + 45) % 360;
    if (hue >= 258 && hue <= 288) hue = (hue + 45) % 360;
    return {
        'background-color': `hsl(${hue}, 72%, 95%)`,
        'color': `hsl(${hue}, 60%, 32%)`,
        'border-color': `hsl(${hue}, 65%, 80%)`,
    };
}

/**
 * The chip kind for a row that HAS a unified channel. `paid` is decided by the caller
 * from the measured signals (utm medium / click ids), because the name never says.
 */
export function acquisitionKindFor(channel: string, campaign?: string | null, paid = false): AcquisitionChipKind {
    if (campaign === SELF_REPORTED_CAMPAIGN) return 'self';
    if (paid) return 'paid';
    if (channel === 'Direct') return 'direct';
    if (channel === 'Referral') return 'referral';
    return 'organic';
}

/** € with no cents once past 1k, so table columns stay narrow and scannable. */
export function formatEuros(value?: number | null): string {
    const n = Number(value || 0);
    if (n === 0) return '€0';
    if (Math.abs(n) >= 1000) return '€' + Math.round(n).toLocaleString('en-US');
    return '€' + n.toFixed(2).replace(/\.00$/, '');
}

export function formatNumber(value?: number | null): string {
    return Number(value || 0).toLocaleString('en-US');
}

export function formatCredits(value?: number | null): string {
    const n = Number(value || 0);
    return (Math.round(n * 10) / 10).toLocaleString('en-US');
}

/** ISO date -> 'Mar 3, 2026'. Empty string for missing dates so the UI shows '—'. */
export function formatDate(value?: string | Date | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** ISO date -> { date: 'Mar 3, 2026', time: '14:08' }. Used where the exact minute matters. */
export function formatDateTimeParts(value?: string | Date | null): { date: string; time: string } {
    if (!value) return { date: '', time: '' };
    const d = new Date(value);
    if (isNaN(d.getTime())) return { date: '', time: '' };
    return {
        date: d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
    };
}

/** Short axis label for a daily bucket ('2026-03-01' -> 'Mar 1'). */
export function formatDayLabel(day: string): string {
    const d = new Date(day + 'T00:00:00');
    if (isNaN(d.getTime())) return day;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function toInputDate(d: Date): string {
    return d.toISOString().split('T')[0];
}

/**
 * Fills every missing day between the first and last bucket with zeros.
 *
 * The API only returns days that actually have rows, so plotting it raw makes a line chart
 * interpolate straight through empty stretches — a month with no signups would read as a
 * gentle slope instead of a flat zero. Densifying keeps the time axis honest.
 *
 * `rows` must be sorted ascending by `date` (the API returns them that way).
 */
export function densifyDaily<T extends { date: string }>(rows: T[], zero: Omit<T, 'date'>): T[] {
    if (!rows || rows.length < 2) return rows || [];

    const byDate = new Map(rows.map(r => [r.date, r]));
    const start = new Date(rows[0].date + 'T00:00:00Z');
    const end = new Date(rows[rows.length - 1].date + 'T00:00:00Z');
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return rows;

    // A very long span at daily resolution would be unreadable and slow; leave it alone
    // and let the axis skip labels instead.
    const spanDays = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (spanDays > 400) return rows;

    const filled: T[] = [];
    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
        const key = d.toISOString().split('T')[0];
        filled.push(byDate.get(key) || ({ date: key, ...zero } as T));
    }
    return filled;
}
